package co.maytes.standupbot.dashboard.features.analytics

import kotlinx.datetime.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.todayIn
import kotlinx.datetime.toLocalDateTime

sealed interface DateRange {
    val preset: Preset
    val from: LocalDate
    val to: LocalDate
    val label: String
    val cacheKey: String
        get() = "${preset.id}:$from:$to"
    val totalDays: Int
        get() = (to.toEpochDays() - from.toEpochDays() + 1).coerceAtLeast(1)

    fun toQuery(): String = when (this) {
        is Relative -> "?days=$totalDays"
        is Custom, is ThisWeek, is Last2Weeks, is ThisMonth -> "?from=$from&to=$to"
    }

    enum class Preset(val id: String, val label: String) {
        D7("7d", "7 days"),
        D30("30d", "30 days"),
        D90("90d", "90 days"),
        THIS_WEEK("this-week", "This week"),
        LAST_2_WEEKS("2w", "Last 2 weeks"),
        THIS_MONTH("this-month", "This month"),
        CUSTOM("custom", "Custom"),
    }

    data class Relative(val days: Int, override val preset: Preset) : DateRange {
        override val to: LocalDate = today()
        override val from: LocalDate = to.minus(days.toLong() - 1, DateTimeUnit.DAY)
        override val label: String = "Last ${preset.label}"
    }

    data class Custom(override val from: LocalDate, override val to: LocalDate) : DateRange {
        override val preset: Preset = Preset.CUSTOM
        override val label: String = "$from – $to"
    }

    data class ThisWeek(val referenceToday: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())) : DateRange {
        override val preset = Preset.THIS_WEEK
        override val to: LocalDate = referenceToday
        override val from: LocalDate = run {
            val daysFromMonday = (referenceToday.dayOfWeek.ordinal) % 7
            referenceToday.minus(daysFromMonday.toLong(), DateTimeUnit.DAY)
        }
        override val label = "This week"
    }

    data class Last2Weeks(val referenceToday: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())) : DateRange {
        override val preset = Preset.LAST_2_WEEKS
        override val to: LocalDate = referenceToday
        override val from: LocalDate = referenceToday.minus(13L, DateTimeUnit.DAY)
        override val label = "Last 2 weeks"
    }

    data class ThisMonth(val referenceToday: LocalDate = Clock.System.todayIn(TimeZone.currentSystemDefault())) : DateRange {
        override val preset = Preset.THIS_MONTH
        override val to: LocalDate = referenceToday
        override val from: LocalDate = LocalDate(referenceToday.year, referenceToday.monthNumber, 1)
        override val label = "This month"
    }

    companion object {
        fun last7(): DateRange = Relative(7, Preset.D7)
        fun last30(): DateRange = Relative(30, Preset.D30)
        fun last90(): DateRange = Relative(90, Preset.D90)
        fun thisWeek(): DateRange = ThisWeek()
        fun last2Weeks(): DateRange = Last2Weeks()
        fun thisMonth(): DateRange = ThisMonth()
        fun default(): DateRange = last30()

        private fun today(): LocalDate =
            Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    }
}
